// Automatic weekly-plan generation coordinator (P0 2026-10-18).
//
// Production 18.10: App.js renders MainAppContent as a component DEFINED INSIDE App, so every App state change
// (e.g. the plan-regen banner) remounts the whole page — WeeklyPlan included — and its per-mount ref
// (bgSyncStartedRef) resets. While week +3 could not be saved (the pilot's plan freeze), every remount fired a
// new regenerate-all job: one every ~9 s, 60+ in minutes, nothing saved.
//
// This module is the single place that decides whether a MISSING week may be generated automatically. Its state
// survives remounts (module-level) and reloads / other tabs (localStorage), so a remount, an effect re-run or a
// polling cycle can never re-fire the same week:
//   - one request at a time per week (concurrent callers share the same promise)
//   - a PERMANENT failure (plan_frozen, persist_failed, not_persisted, authorization, profile) is never retried
//     automatically — only by an explicit manual retry
//   - a TRANSIENT failure (network, timeout, 5xx) is retried at most MAX_TRANSIENT times with backoff
//   - success counts only when the plan is read back from the server (verify), not when a flag says so
// A slot is (user, sport, week START DATE): next Sunday the same "week +3" slot is a new week and starts clean.

const STORE_KEY = 'ritmo.autoPlanRegen.v1';
export const BACKOFF_MS = [60 * 1000, 5 * 60 * 1000, 15 * 60 * 1000];
export const MAX_TRANSIENT = BACKOFF_MS.length;
export const PENDING_STALE_MS = 10 * 60 * 1000;        // a 'pending' older than this was abandoned (tab closed)
export const PERMANENT_HOLD_MS = 12 * 60 * 60 * 1000;  // a permanent failure is not auto-retried for 12 h
// auto_retry_limit: the server's own bound on AUTOMATIC attempts (3 failed per hour for the same weeks) — holds even
// when this browser cannot keep its counter (private mode / cleared storage)
export const PERMANENT_CODES = ['plan_frozen', 'persist_failed', 'not_persisted', 'blocked', 'no_profile',
                                'profile_incomplete', 'unauthorized', 'forbidden', 'auto_retry_limit'];

const memory = new Map();          // userId -> { [slotKey]: entry }   (fallback when storage is unavailable)
const inflight = new Map();        // `${userId}|${slotKey}` -> Promise

function nowMs(now) {
  return typeof now === 'number' ? now : Date.now();
}

function readAll() {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return null;
  }
}

function load(userId) {
  const all = readAll();
  if (all && all[userId]) return { ...all[userId] };
  return { ...(memory.get(userId) || {}) };
}

function save(userId, entries) {
  memory.set(userId, entries);
  try {
    const all = readAll() || {};
    all[userId] = entries;
    window.localStorage.setItem(STORE_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable (private mode): the module-level memory still protects this page */
  }
}

export function slotKey(sport, weekStart) {
  return `${sport}|${weekStart}`;
}

/** {code, permanent} from a planRegenRequest result (backend codes travel as "error" or "[code]" in the text). */
export function classifyFailure(result) {
  const text = `${result?.error || ''} ${result?.message || ''}`;
  const bracket = text.match(/\[([a-z_]+)\]/);
  let code = bracket ? bracket[1] : (PERMANENT_CODES.find((c) => text.includes(c)) || null);
  if (!code && /מוקפאת/.test(text)) code = 'plan_frozen';
  if (!code && /לא נשמרה/.test(text)) code = 'persist_failed';
  if (!code && /HTTP 40[13]/.test(text)) code = 'unauthorized';
  if (!code) code = /polling_timeout/.test(text) ? 'polling_timeout'
    : (/fetch_failed|network|Failed to fetch|HTTP 5\d\d|llm_timeout|timeout/i.test(text) ? 'transient' : 'unknown');
  return { code, permanent: PERMANENT_CODES.includes(code) };
}

/** Is this slot allowed to be generated AUTOMATICALLY right now? */
export function autoAllowed(entry, now) {
  const t = nowMs(now);
  if (!entry) return true;
  if (entry.state === 'pending') return t - entry.at > PENDING_STALE_MS;
  if (entry.state === 'failed_permanent') return t - entry.at > PERMANENT_HOLD_MS;
  if (entry.state === 'failed_transient') return entry.attempts < MAX_TRANSIENT && t >= entry.nextAt;
  return true;   // 'succeeded' but missing again (e.g. deleted) -> a fresh attempt
}

/** Slots that need the athlete's attention (permanent failure, or transient retries exhausted). */
export function blockedSlots(userId, slots, now) {
  const entries = load(userId);
  return slots.filter((s) => {
    const e = entries[slotKey(s.sport, s.weekStart)];
    if (!e) return false;
    if (e.state === 'failed_permanent') return !autoAllowed(e, now);
    return e.state === 'failed_transient' && e.attempts >= MAX_TRANSIENT;
  }).map((s) => ({ ...s, ...entries[slotKey(s.sport, s.weekStart)] }));
}

/** Forget the recorded outcome of these slots (the manual "retry" action). */
export function resetSlots(userId, slots) {
  const entries = load(userId);
  slots.forEach((s) => { delete entries[slotKey(s.sport, s.weekStart)]; });
  save(userId, entries);
}

function record(userId, slots, patch) {
  const entries = load(userId);
  slots.forEach((s) => {
    const k = slotKey(s.sport, s.weekStart);
    entries[k] = { ...(typeof patch === 'function' ? patch(entries[k]) : patch) };
  });
  save(userId, entries);
}

/**
 * Generate the missing slots once, under the rules above.
 *   request(slots)  -> planRegenRequest-shaped result {success, status, error, message}
 *   verify(slots)   -> Set of slotKey that are now really present on the server
 *   manual          -> an explicit athlete action: ignores the automatic holds (still never two at once)
 * Returns {ran, slots, result, outcome: 'succeeded'|'failed_permanent'|'failed_transient'|'not_needed', blocked}.
 */
export async function runAutoRegen({ userId, slots, request, verify, manual = false, now }) {
  const t = nowMs(now);
  const entries = load(userId);
  const todo = slots.filter((s) => {
    const key = `${userId}|${slotKey(s.sport, s.weekStart)}`;
    if (inflight.has(key)) return false;
    return manual || autoAllowed(entries[slotKey(s.sport, s.weekStart)], t);
  });
  if (!todo.length) {
    const shared = slots.map((s) => inflight.get(`${userId}|${slotKey(s.sport, s.weekStart)}`)).find(Boolean);
    if (shared) return shared;
    return { ran: false, slots: [], outcome: 'not_needed', blocked: blockedSlots(userId, slots, t) };
  }
  record(userId, todo, (prev) => ({ state: 'pending', at: t, attempts: (prev && prev.attempts) || 0 }));
  const job = (async () => {
    let result;
    try {
      result = await request(todo);
    } catch (e) {
      result = { success: false, status: 'failed', error: e?.message || 'fetch_failed' };
    }
    if (result && result.success) {
      let present = new Set();
      try { present = await verify(todo); } catch { present = new Set(); }
      const saved = todo.filter((s) => present.has(slotKey(s.sport, s.weekStart)));
      const notSaved = todo.filter((s) => !present.has(slotKey(s.sport, s.weekStart)));
      record(userId, saved, { state: 'succeeded', at: nowMs() });
      // a "success" whose plan cannot be read back is the 18.10 bug itself: permanent until a manual retry
      record(userId, notSaved, { state: 'failed_permanent', code: 'not_persisted', at: nowMs() });
      return { ran: true, slots: todo, result, outcome: notSaved.length ? 'failed_permanent' : 'succeeded',
               blocked: blockedSlots(userId, slots) };
    }
    const { code, permanent } = classifyFailure(result);
    if (permanent) {
      record(userId, todo, { state: 'failed_permanent', code, message: result?.message || result?.error, at: nowMs() });
    } else {
      record(userId, todo, (prev) => {
        const attempts = ((prev && prev.attempts) || 0) + 1;
        return { state: 'failed_transient', code, attempts, at: nowMs(),
                 nextAt: nowMs() + BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)] };
      });
    }
    return { ran: true, slots: todo, result, outcome: permanent ? 'failed_permanent' : 'failed_transient',
             blocked: blockedSlots(userId, slots) };
  })();
  todo.forEach((s) => inflight.set(`${userId}|${slotKey(s.sport, s.weekStart)}`, job));
  try {
    return await job;
  } finally {
    todo.forEach((s) => inflight.delete(`${userId}|${slotKey(s.sport, s.weekStart)}`));
  }
}

/** Test helper: forget everything (module memory + storage). */
export function _resetForTests() {
  memory.clear();
  inflight.clear();
  try { window.localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
}
